import { Flex } from "src/components/Flex";
import { COLLAPSED_MENU_WIDTH, MENU_WIDTH } from "./Menu";
import { Button, Spin, Tooltip, theme, Typography, Divider } from "antd";
import { useApplicationContext } from "src/hooks/useApplication";
import Link from "next/link";
import { SidebarIcon } from "src/components/SidebarIcon";
import {
  AppstoreAddOutlined,
  ArrowLeftOutlined,
  FormatPainterOutlined,
  LoginOutlined,
  ScissorOutlined,
  ShareAltOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { MenuLink } from "./MainMenu";
import { VzblIcon } from "src/components/VzblIcon";
import { BuildingIcon } from "src/components/BuildingIcon";
import { MENU_TYPE } from "src/hooks/useMenu";
import { useRouter } from "next/router";
import typography from "../src/components/typography.module.css";

const { Text } = Typography;

export const MenuSettings = ({
  orgId,
  loading,
  setMenu,
}: {
  orgId?: string;
  loading: boolean;
  setMenu: (menu: MENU_TYPE) => void;
}) => {
  const { token } = theme.useToken();
  const app = useApplicationContext();
  const isUser = app.role === "user";

  const CategoryHeader = ({ title }: { title: string }) =>
    app.sidebar.isOpen ? (
      <Text className={`${typography.textLightSolid}`}>{title}</Text>
    ) : null;

  return (
    <>
      <Flex
        centered
        className="main-menu"
        style={{ position: "relative", color: token.colorTextLightSolid }}
      >
        <div
          className="js-menu menu-container"
          style={{
            width: app.sidebar.isOpen ? MENU_WIDTH : COLLAPSED_MENU_WIDTH,
          }}
        >
          <div style={{ flex: 0, height: 48 }}>
            <BackButton setMenu={setMenu} />
          </div>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              padding: 8,
              overflow: "auto",
              overflowX: "hidden",
            }}
          >
            {/* Personal Section */}
            <CategoryHeader title="Personal" />
            <MenuLink
              root
              icon={<FormatPainterOutlined />}
              label="Preferences"
              href={`/${orgId}/settings/preferences`}
            />
            <MenuLink
              root
              icon={<LoginOutlined />}
              label="Logins"
              href={`/${orgId}/settings/logins`}
            />

            {/* Organization Section */}
            {app.isOrgAdmin && (
              <>
                <div style={{ height: 16 }} />
                <CategoryHeader title="Organization" />
                <MenuLink
                  root
                  icon={<AppstoreAddOutlined />}
                  label="Integrations"
                  href={`/${orgId}/settings/integrations`}
                />
                {app.isSuperAdmin && (
                  <MenuLink
                    root
                    icon={<TrophyOutlined />}
                    label="Competency Management"
                    href={`/${orgId}/settings/competencies`}
                  />
                )}
                <MenuLink
                  root
                  icon={<BuildingIcon size={14} />}
                  label="Users"
                  href={`/${orgId}/settings/users`}
                />
                <MenuLink
                  root
                  icon={<UserOutlined />}
                  label="Members"
                  href={`/${orgId}/settings/identities`}
                />
                {(app.isOrgAdmin || isUser) && (
                  <MenuLink
                    root
                    icon={<ScissorOutlined />}
                    label="Snippets"
                    href={`/${orgId}/settings/snippets`}
                  />
                )}
              </>
            )}

            {/* System Section */}
            {app.isSuperAdmin && (
              <>
                <div style={{ height: 16 }} />
                <CategoryHeader title="System" />
                <MenuLink
                  root
                  icon={<VzblIcon style={{ fill: "currentColor" }} />}
                  label="Super Admin"
                  href={`/${orgId}/settings/system`}
                />
              </>
            )}
            {/* Organization Section */}
            {app.isOrgAdmin && (
              <>
                <div style={{ height: 16 }} />
                <CategoryHeader title="Subscription" />
                <MenuLink
                  root
                  icon={<VzblIcon style={{ fill: "currentColor" }} />}
                  label="Manage"
                  href={`/${orgId}/settings/subscriptions`}
                />
              </>
            )}
          </div>
        </div>
      </Flex>
    </>
  );
};

function BackButton({ setMenu }: { setMenu: (menu: MENU_TYPE) => void }) {
  const { isOpen, toggle } = useApplicationContext().sidebar;
  const router = useRouter();
  const app = useApplicationContext();

  const handleBack = () => {
    // First try to go back to main menu
    setMenu("main");
  };

  return (
    <div
      className="js-menu-main"
      style={{
        alignItems: "flex-start",
        borderRadius: 16,
        display: "flex",
        fontSize: 12,
        fontWeight: 500,
        height: "100%",
        justifyContent: isOpen ? "space-between" : "center",
        overflow: isOpen ? undefined : "hidden",
        padding: "12px 8px 8px",
        width: isOpen ? MENU_WIDTH : COLLAPSED_MENU_WIDTH,
      }}
    >
      {isOpen && (
        <div style={{ marginLeft: 8 }}>
          <Tooltip title="Back to Main Menu" placement="bottomRight">
            <Button
              size="small"
              shape="circle"
              type="text"
              style={{ color: "inherit", border: "none" }}
              onClick={handleBack}
              icon={<ArrowLeftOutlined style={{ marginTop: 4 }} />}
            />
          </Tooltip>{" "}
          <span style={{ fontSize: 14 }}>Settings</span>
        </div>
      )}
      <Tooltip
        open={isOpen ? false : undefined}
        title="Toggle application sidebar"
        placement="right"
        mouseEnterDelay={isOpen ? 60 : 0}
      >
        <Button
          size="small"
          shape="circle"
          type="text"
          style={{ color: "inherit", border: "none" }}
          onClick={() => toggle()}
          icon={<SidebarIcon style={{ marginTop: 4 }} />}
        />
      </Tooltip>
    </div>
  );
}
